import React, { useMemo } from 'react';
import TopicsSection from './TopicsSection';
import { signalLabel } from './signalLabels';

// ── Signals → topics/stats adapters ───────────────────────────────────────────
// The report API returns `signal_results` in this shape:
//
//   { open_text_questions: {
//       "<question>": { pillar: "<pillar>", signals: {
//         "<signal>": { is_negative, total_flagged, attr_count: {
//           "<filter group>": { "<choice>": { count, diff, pct_in_all,
//             total_all_answered, ... } } } } } } } }
//
// TopicsSection's heatmap renders the same structure the report topics use, so we
// reshape signals into that structure and render TopicsSection in "signals" mode.

// Signals flagged in only a handful of responses are too thin to read anything
// into, so they are left out of the table entirely. The threshold applies per
// question, against that question's own total_flagged: a signal hidden under one
// question still shows under another where enough responses were flagged.
const MIN_SIGNAL_RESPONSES = 5;
const hasEnoughResponses = (sig) => (sig?.total_flagged ?? 0) >= MIN_SIGNAL_RESPONSES;

// Each signal becomes a "topic" row under its question, inheriting the question's
// pillar and carrying is_negative. attr_count is flattened by choice text (the
// flat shape the heatmap reads); choice texts are unique across filter groups.
const signalsToTopicsData = (signalsJson, language) => {
    const openTextQuestions = signalsJson?.open_text_questions || {};
    const result = {};
    Object.entries(openTextQuestions).forEach(([questionText, entry]) => {
        const pillar = entry?.pillar ?? null;
        const signals = entry?.signals || {};
        const rows = Object.entries(signals)
            .filter(([, sig]) => hasEnoughResponses(sig))
            .map(([signalName, sig]) => {
                const attr_topic_count = {};
                const attr_diff = {};
                // Full per-choice cell kept verbatim so the table shows the JSON's own
                // numbers (count, diff, pct_in_signal, pct_in_all, totals) rather than
                // recomputing them. Consumed only in the signals variant.
                const attr_cells = {};
                Object.values(sig?.attr_count || {}).forEach((group) => {
                    Object.entries(group || {}).forEach(([choiceText, cell]) => {
                        attr_topic_count[choiceText] = cell?.count ?? 0;
                        attr_diff[choiceText] = cell?.diff ?? 0;
                        attr_cells[choiceText] = cell || {};
                    });
                });
                return {
                    // Labelled in the REPORT's language, not the UI's — the BE
                    // sends canonical English keys ("confusion"), which is the
                    // model's feature contract and not a display string.
                    title: signalLabel(signalName, language),
                    count: sig?.total_flagged ?? 0,
                    pillar,
                    is_negative: sig?.is_negative ?? false,
                    keywords: {},
                    responses: [],
                    attr_diff,
                    attr_topic_count,
                    attr_cells,
                };
            });
        // Drop the question altogether when nothing survives the threshold —
        // an empty key would still count towards the per-question divisors
        // TopicsSection derives from topicsData.
        if (rows.length > 0) result[questionText] = rows;
    });
    return result;
};

// Build the demographics (statsData) from the signals JSON itself: each attr_count
// group is a filter question, each key a choice. responses_count (the Y in the
// "X / Y Responded" header) is pct_in_all * total_all_answered. Definitions are
// consistent across signals, so we take the first occurrence of each.
const signalsToStatsData = (signalsJson) => {
    const openTextQuestions = signalsJson?.open_text_questions || {};
    const groups = new Map(); // groupTitle -> Map(choiceText -> responses_count)
    Object.values(openTextQuestions).forEach((entry) => {
        // Same threshold as the rows: a signal that is not shown must not be the
        // one that introduces a demographic column to the header.
        Object.values(entry?.signals || {}).filter(hasEnoughResponses).forEach((sig) => {
            Object.entries(sig?.attr_count || {}).forEach(([groupTitle, choices]) => {
                if (!groups.has(groupTitle)) groups.set(groupTitle, new Map());
                const choiceMap = groups.get(groupTitle);
                Object.entries(choices || {}).forEach(([choiceText, cell]) => {
                    if (!choiceMap.has(choiceText)) {
                        const total = Math.round((cell?.pct_in_all ?? 0) * (cell?.total_all_answered ?? 0));
                        choiceMap.set(choiceText, total);
                    }
                });
            });
        });
    });
    return {
        questions: Array.from(groups.entries()).map(([title, choiceMap]) => ({
            title,
            choices: Array.from(choiceMap.entries()).map(([text, responses_count]) => ({
                text,
                responses_count,
            })),
        })),
    };
};

// Thin wrapper: reshape signal_results and render the shared heatmap in signals
// mode. The pillar to show is controlled by the parent (Risk tab) via selectedPillar.
const SignalsTable = ({ signalResults, reportId, reportName, selectedPillar, language }) => {
    const topicsData = useMemo(
        () => (signalResults ? signalsToTopicsData(signalResults, language) : {}),
        [signalResults, language]
    );
    const statsData = useMemo(
        () => (signalResults ? signalsToStatsData(signalResults) : null),
        [signalResults]
    );

    return (
        <TopicsSection
            variant="signals"
            topicsData={topicsData}
            statsData={statsData}
            reportId={reportId}
            reportName={reportName}
            externalSelectedPillar={selectedPillar}
            language={language}
        />
    );
};

// Distinct pillars present in the signals payload, in first-seen order.

export default SignalsTable;
